import React, { useState, useEffect } from "react";
import { useUploadManager, makeUploadKey } from "./UploadManager";
import API_BASE_URL from "../config";
import "./FolderManager.css";

// authToken: a co-worker link session (UploadLinkPage). Omitted for the studio's own
// uploads, which use the login token.
export function FolderCard({ eventId, eventName, folderId, name, imageCount, deletable, onDelete, renamable, onRename, isProMax, authToken = null }) {
  const uploadManager = useUploadManager();
  const [dragActive, setDragActive] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(name);
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState("");
  // Pro Max defaults to "original"; non-Pro Max always compressed (no toggle shown)
  const [uploadMode, setUploadMode] = useState("compressed");
  useEffect(() => {
    if (isProMax) setUploadMode("original");
  }, [isProMax]);

  const key = makeUploadKey(eventId, folderId);
  const upload = uploadManager.uploads[key] || {};
  const isUploading = !!upload.isUploading;

  const handleFiles = (files) => {
    if (!files || files.length === 0) return;
    uploadManager.startUpload(
      eventId, eventName, files, folderId, name,
      uploadMode === "original",           // skipCompression
      isProMax && uploadMode === "compressed", // proMaxCompressed — lighter 1–2 MB compression
      authToken
    );
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const startEditing = () => {
    setEditName(name);
    setRenameError("");
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setRenameError("");
  };

  const saveEditing = async (e) => {
    e.preventDefault();
    const trimmed = editName.trim();
    if (!trimmed || trimmed === name) { setEditing(false); return; }
    setRenaming(true);
    setRenameError("");
    try {
      await onRename(trimmed);
      setEditing(false);
    } catch (err) {
      setRenameError(err.message);
    } finally {
      setRenaming(false);
    }
  };

  return (
    <div className="fm-card">
      {editing ? (
        <form className="fm-card-rename-row" onSubmit={saveEditing}>
          <input
            type="text"
            className="fm-rename-input"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            maxLength={100}
            autoFocus
            disabled={renaming}
          />
          <button type="submit" className="fm-mini-btn fm-rename-save" disabled={renaming || !editName.trim()}>
            {renaming ? "..." : "✓"}
          </button>
          <button type="button" className="fm-mini-btn fm-rename-cancel" onClick={cancelEditing} disabled={renaming}>
            ✕
          </button>
        </form>
      ) : (
        <div className="fm-card-header">
          <span className="fm-card-name">📁 {name}</span>
          <span className="fm-card-count">{imageCount ?? 0} photo{imageCount === 1 ? "" : "s"}</span>
          {renamable && (
            <button className="fm-card-edit" title="Rename folder" onClick={startEditing}>✏️</button>
          )}
          {deletable && (
            <button className="fm-card-delete" title="Delete folder" onClick={onDelete}>🗑</button>
          )}
        </div>
      )}
      {renameError && <span className="fm-add-error">{renameError}</span>}

      {isProMax && (
        <div className="fm-quality-toggle">
          <button
            className={`fm-quality-btn ${uploadMode === "compressed" ? "active" : ""}`}
            onClick={() => setUploadMode("compressed")}
            type="button"
          >
            📦 Compressed
          </button>
          <button
            className={`fm-quality-btn ${uploadMode === "original" ? "active" : ""}`}
            onClick={() => setUploadMode("original")}
            type="button"
          >
            ✨ Original
          </button>
        </div>
      )}

      <div
        className={`fm-dropzone ${dragActive ? "active" : ""}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          multiple
          accept={isProMax ? "image/*,.cr2,.cr3,.nef,.arw,.dng,.orf,.raf,.rw2,.pef,.srw,.x3f,.3fr,.mrw,.nrw,.rwl" : "image/*"}
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          className="fm-file-input"
          disabled={isUploading}
        />
        <span className="fm-dropzone-text">
          {isUploading ? (upload.statusText || "Uploading...") : isProMax ? "☁️ Drop photos or RAW files, or click to browse" : "☁️ Drop photos or click to browse"}
        </span>
      </div>

      {isUploading && (
        <div className="fm-progress-container">
          <div className="fm-progress-bar">
            <div className="fm-progress-fill" style={{ width: `${upload.progress || 0}%` }} />
          </div>
          <div className="fm-progress-row">
            <span className="fm-progress-text">{Math.round(upload.progress || 0)}%</span>
            <button className="fm-mini-btn fm-pause" onClick={() => uploadManager.pauseResume(eventId, folderId)}>
              {upload.isPaused ? "Resume" : "Pause"}
            </button>
            <button className="fm-mini-btn fm-cancel" onClick={() => uploadManager.cancelUpload(eventId, folderId)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {!isUploading && upload.error && (
        <div className="fm-message fm-error">
          {upload.error}
          {upload.failedCount > 0 && upload.lastFinishedJobId && (
            <button className="fm-mini-btn fm-retry" onClick={() => uploadManager.retryFailed(eventId, folderId)}>
              🔄 Retry
            </button>
          )}
        </div>
      )}
      {!isUploading && upload.success && (
        <div className="fm-message fm-success">{upload.success}</div>
      )}
    </div>
  );
}

function FolderManager({
  eventId, eventName, folders, onFolderCreated, onFolderDeleted, onFolderRenamed,
  generalCount, generalFolderName, onGeneralFolderRenamed, isProMax,
}) {
  const [newFolderName, setNewFolderName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const handleCreate = async (e) => {
    e.preventDefault();
    const name = newFolderName.trim();
    if (!name) return;
    setCreating(true);
    setCreateError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/folders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to create folder");
      onFolderCreated(data.folder);
      setNewFolderName("");
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (folder) => {
    const count = folder.image_count || 0;
    const photoWarning = count > 0
      ? ` This will also permanently delete its ${count} photo${count === 1 ? "" : "s"}.`
      : "";
    if (!window.confirm(`Delete folder "${folder.name}"?${photoWarning} This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/folders/${folder.id}`, { method: "DELETE" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete folder");
      onFolderDeleted(folder.id, data.deletedImages || 0);
    } catch (err) {
      window.alert(err.message);
    }
  };

  const handleRename = async (folder, newName) => {
    const res = await fetch(`${API_BASE_URL}/api/events/folders/${folder.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newName }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Failed to rename folder");
    onFolderRenamed(folder.id, data.folder.name);
  };

  const handleRenameGeneral = async (newName) => {
    const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/general-folder-name`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newName }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Failed to rename folder");
    onGeneralFolderRenamed(data.general_folder_name);
  };

  return (
    <div className="fm-section">
      <div className="fm-grid">
        <FolderCard
          eventId={eventId}
          eventName={eventName}
          folderId={null}
          name={generalFolderName || "General"}
          imageCount={generalCount}
          deletable={false}
          renamable={true}
          onRename={handleRenameGeneral}
          isProMax={isProMax}
        />
        {folders.map((f) => (
          <FolderCard
            key={f.id}
            eventId={eventId}
            eventName={eventName}
            folderId={f.id}
            name={f.name}
            imageCount={f.image_count}
            deletable={true}
            onDelete={() => handleDelete(f)}
            renamable={true}
            onRename={(newName) => handleRename(f, newName)}
            isProMax={isProMax}
          />
        ))}

        <form className="fm-card fm-add-card" onSubmit={handleCreate}>
          <span className="fm-card-name">➕ New Folder</span>
          <input
            type="text"
            className="fm-add-input"
            placeholder="e.g., Candid, Traditional..."
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            maxLength={100}
          />
          <button type="submit" className="fm-add-btn" disabled={creating || !newFolderName.trim()}>
            {creating ? "Creating..." : "Create Folder"}
          </button>
          {createError && <span className="fm-add-error">{createError}</span>}
        </form>
      </div>
    </div>
  );
}

export default FolderManager;
